import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PersonalSkillDraft,
  ResolvedSkillDetail,
  ResolvedSkillMetadata,
  SkillSettingsEntry,
} from '@hector/shared-types';
import { useAuth } from '@/components/AuthProvider';
import { skillsApi } from './skillsApi';

export const SKILLS_QUERY_KEY = ['agent-skills'] as const;
export const skillsQueryKeys = {
  settings: (userId: string | null) => [...SKILLS_QUERY_KEY, userId, 'settings'] as const,
  catalog: (userId: string | null) => [
    ...SKILLS_QUERY_KEY,
    userId,
    'catalog',
    'casediscovery',
  ] as const,
};

const EMPTY_SETTINGS: SkillSettingsEntry[] = [];
const EMPTY_CATALOG: ResolvedSkillMetadata[] = [];

async function invalidateSkillQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string | null,
): Promise<void> {
  // Both Settings and slash consumers may be mounted. Explicit specific + general
  // invalidation keeps the convention visible and makes WS mutations share it.
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: skillsQueryKeys.settings(userId) }),
    queryClient.invalidateQueries({ queryKey: skillsQueryKeys.catalog(userId) }),
    queryClient.invalidateQueries({ queryKey: SKILLS_QUERY_KEY }),
  ]);
}

export interface UseSkillSettingsReturn {
  skills: SkillSettingsEntry[];
  isLoading: boolean;
  error: Error | null;
  isMutating: boolean;
  createSkill(draft: PersonalSkillDraft): Promise<ResolvedSkillDetail>;
  updateSkill(currentSlug: string, draft: PersonalSkillDraft): Promise<ResolvedSkillDetail>;
  setSkillActive(slug: string, isActive: boolean): Promise<ResolvedSkillDetail>;
  resetSkill(slug: string): Promise<ResolvedSkillDetail>;
  deleteSkill(slug: string): Promise<void>;
}

export function useSkillSettings(): UseSkillSettingsReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: skillsQueryKeys.settings(userId),
    queryFn: skillsApi.listSettings,
    enabled: userId !== null,
  });
  const create = useMutation({
    mutationFn: skillsApi.create,
    onSuccess: () => invalidateSkillQueries(queryClient, userId),
  });
  const update = useMutation({
    mutationFn: ({ currentSlug, draft }: { currentSlug: string; draft: PersonalSkillDraft }) =>
      skillsApi.update(currentSlug, draft),
    onSuccess: () => invalidateSkillQueries(queryClient, userId),
  });
  const active = useMutation({
    mutationFn: ({ slug, isActive }: { slug: string; isActive: boolean }) =>
      skillsApi.setActive(slug, isActive),
    onSuccess: () => invalidateSkillQueries(queryClient, userId),
  });
  const remove = useMutation({
    mutationFn: skillsApi.delete,
    onSuccess: () => invalidateSkillQueries(queryClient, userId),
  });
  const reset = useMutation({
    mutationFn: (slug: string) => skillsApi.reset(slug),
    onSuccess: () => invalidateSkillQueries(queryClient, userId),
  });

  return {
    skills: query.data ?? EMPTY_SETTINGS,
    isLoading: query.isLoading,
    error: query.error,
    isMutating: create.isPending
      || update.isPending
      || active.isPending
      || reset.isPending
      || remove.isPending,
    createSkill: create.mutateAsync,
    updateSkill: (currentSlug, draft) => update.mutateAsync({ currentSlug, draft }),
    setSkillActive: (slug, isActive) => active.mutateAsync({ slug, isActive }),
    resetSkill: reset.mutateAsync,
    deleteSkill: remove.mutateAsync,
  };
}

export function useSkillCatalog(): {
  skills: ResolvedSkillMetadata[];
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const query = useQuery({
    queryKey: skillsQueryKeys.catalog(userId),
    queryFn: skillsApi.listCatalog,
    enabled: userId !== null,
  });
  return {
    skills: query.data ?? EMPTY_CATALOG,
    isLoading: query.isLoading,
    error: query.error,
  };
}
